import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import { withStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Tooltip } from "@material-ui/core";
import {
  fetchRedirectionParams,
  fetchRedirectionParamsForVpos
} from "../../../actions/redirect";
import { setCardData } from "../../../actions/card";
import { FormatAmount } from "../../../utils/Formatter";
import { calculateAmount } from "../../../utils/SurchargeUtils";
import CardInput from "./cardinput/CardInput";
import ExpDateInput from "./expdate/ExpDateInput";
import CardInputValidator from "./cardinput/CardInputValidator";
import ExpDateValidator from "./expdate/ExpDateValidator";
import "./CardForm.css";
import VposCardInputValidator from "./cardinput/VposCardInputValidator";

const minLengthValidator = minLength => {
  return value => {
    if (value.length < minLength) {
      return { showError: true, errorMsg: undefined };
    }
    return { showError: false, errorMsg: undefined };
  };
};

const styles = theme => ({
  payBtnColor: {
    color: theme.palette.success.main,
    borderColor: theme.palette.success.main,
    border: `solid`
  }
});

export class CardForm extends React.Component {
  constructor(props) {
    super(props);
    const { charge, isVpos } = props;
    this.refList = {
      expDate: React.createRef(),
      cvv: React.createRef(),
      cardHolderName: React.createRef(),
      title: React.createRef()
    };

    this.fields = {
      cardNumber: {
        validator: isVpos ? VposCardInputValidator : CardInputValidator,
        minLength: 19,
        maxLength: 19,
        nextRef: this.refList.expDate
      },
      expDate: {
        validator: ExpDateValidator,
        minLength: 7,
        maxLength: 7,
        nextRef: this.refList.cvv
      },
      cvv: {
        validator: minLengthValidator(3),
        minLength: 3,
        maxLength: 4,
        regex: /^\d*$/,
        nextRef: this.refList.cardHolderName
      },
      cardHolderName: {
        validator: minLengthValidator(2),
        regex: /^[a-zA-Z ]*$/,
        minLength: 2,
        maxLength: 32,
        nextRef: this.refList.title
      },
      title: {
        validator: minLengthValidator(0),
        regex: /^[a-zA-Z0-9 '-]*$/,
        minLength: 2,
        maxLength: 32
      }
    };

    this.state = {
      isVpos,
      forceSaveCard: charge.schedule,
      cardNumber: { value: "" },
      expDate: { value: "" },
      cvv: { value: "" },
      cardHolderName: { value: "" },
      saveCard: {
        value: !!charge.customerId
      },
      title: { value: "" }
    };
  }

  handleChange(field, event) {
    const { value } = event.target;
    const { maxLength, nextRef, regex } = this.fields[field];
    if (regex && !regex.test(value)) return;
    if (value.length > maxLength) return;
    if (value.length === maxLength) {
      if (this.validate(field, value) && nextRef) {
        nextRef.current.focus();
      }
    } else {
      this.setState({
        [field]: { value, showError: false, errorMsg: undefined }
      });
    }
  }

  handleCheckboxChange(field, event) {
    const { forceSaveCard } = this.state;
    if (forceSaveCard) return;
    const { checked } = event.target;
    if (!checked) {
      this.setState({ title: { value: "" } });
    }
    this.setState({
      [field]: { value: checked }
    });
  }

  handleBlur(field, event) {
    const { value } = event.target;
    if (!this.validate(field, value)) {
      event.preventDefault();
    }
  }

  validate(field, value) {
    const { isVpos } = this.state;
    const { showError, errorMsg } = this.fields[field].validator(value);

    const showErrorMsgForVposCVV = false;
    if (isVpos && field === "cvv" && value === "") {
      this.setState({ [field]: { value, showErrorMsgForVposCVV, errorMsg } });
      return !showErrorMsgForVposCVV;
    }
    this.setState({ [field]: { value, showError, errorMsg } });
    return !showError;

    // return
  }

  submitCardForm() {
    let invalidForm = false;
    // eslint-disable-next-line no-restricted-syntax,guard-for-in
    for (const field in this.fields) {
      // eslint-disable-next-line react/destructuring-assignment
      if (!this.validate(field, this.state[field].value)) {
        invalidForm = true;
      }
    }
    if (invalidForm) return;

    const { dispatch, charge, history, isVpos } = this.props;
    const {
      cardNumber,
      expDate,
      cvv,
      cardHolderName,
      saveCard,
      title
    } = this.state;
    const expMonth = expDate.value.substr(0, 2);
    const expYear = expDate.value.substr(5);
    const form = {
      cardNumber: cardNumber.value,
      expMonth,
      expYear,
      cvv: cvv.value,
      cardHolderName: cardHolderName.value,
      saveCard: saveCard.value,
      // title: title.value,
      channel: "PAYPAGE"
    };
    if (title.value.length > 0 && title.value) {
      form.title = title.value;
    }
    dispatch(
      !isVpos
        ? fetchRedirectionParams(charge.uuid, form, "card", history)
        : fetchRedirectionParamsForVpos(charge.uuid, form, "card", history)
    );
    form.type = "card";
    dispatch(setCardData(form));
  }

  render() {
    const { charge, redirecting, classes, history } = this.props;
    const {
      cardNumber,
      expDate,
      cvv,
      cardHolderName,
      saveCard,
      title,
      forceSaveCard
    } = this.state;

    const {
      due,
      currency,
      paymentFamilies,
      customerId: canSaveCard,
      feeRenderMode,
      recurring
    } = charge;
    const totalAmount = FormatAmount(
      calculateAmount(
        due,
        history.location.pathname,
        paymentFamilies.surcharges,
        feeRenderMode
      ),
      currency
    );

    return (
      <Grid container className="card-form">
        <Grid item sm={8} xs={12}>
          <CardInput
            className="card-form-field"
            onChange={e => this.handleChange("cardNumber", e)}
            onBlur={e => this.handleBlur("cardNumber", e)}
            value={cardNumber.value}
            showError={cardNumber.showError}
            errorMsg={cardNumber.errorMsg}
          />
        </Grid>
        <Grid item sm={4} xs={12}>
          <ExpDateInput
            className="card-form-field"
            onChange={e => this.handleChange("expDate", e)}
            onBlur={e => this.handleBlur("expDate", e)}
            withRef={this.refList.expDate}
            value={expDate.value}
            showError={expDate.showError}
            errorMsg={expDate.errorMsg}
          />
          <TextField
            InputLabelProps={{ shrink: true }}
            className="card-form-field"
            id="cvv"
            inputProps={{ name: "cvv" }}
            label="cvv"
            style={{ width: "33%" }}
            type="password"
            value={cvv.value}
            error={cvv.showError}
            helperText={cvv.errorMsg}
            onChange={e => this.handleChange("cvv", e)}
            onBlur={e => this.handleBlur("cvv", e)}
            inputRef={this.refList.cvv}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            className="card-form-field"
            id="cardHolderName"
            inputProps={{ name: "cardHolderName" }}
            label="Card Holder Name"
            type="text"
            fullWidth
            value={cardHolderName.value}
            error={cardHolderName.showError}
            helperText={cardHolderName.errorMsg}
            onChange={e => this.handleChange("cardHolderName", e)}
            onBlur={e => this.handleBlur("cardHolderName", e)}
            inputRef={this.refList.cardHolderName}
          />
        </Grid>
        {canSaveCard && (
          <Grid item xs={12}>
            <FormControlLabel
              label={
                forceSaveCard
                  ? "Save card for recurring payments"
                  : "Save card details for next time"
              }
              control={
                <Tooltip
                  enterTouchDelay={0}
                  title={forceSaveCard ? "mandatory for payment plan" : ""}
                >
                  <Switch
                    disabled={recurring}
                    id="saveCard"
                    color="primary"
                    checked={saveCard.value}
                    value="saveCard"
                    onChange={e => this.handleCheckboxChange("saveCard", e)}
                  />
                </Tooltip>
              }
            />
          </Grid>
        )}
        {saveCard.value && (
          <Grid item xs={12}>
            <TextField
              id="title"
              className="card-form-field"
              inputProps={{ name: "title" }}
              label="Title"
              type="text"
              fullWidth
              value={title.value}
              error={title.showError}
              helperText={title.errorMsg}
              onChange={e => this.handleChange("title", e)}
              onBlur={e => this.handleBlur("title", e)}
              inputRef={this.refList.title}
              placeholder="any card title e.g. ABC Bank Card"
            />
          </Grid>
        )}
        <input type="hidden" name="uuid" value={charge.uuid} />
        <Grid item xs={12} style={{ textAlign: "center" }}>
          <Button
            id="pay-button"
            variant="outlined"
            color="primary"
            className={classes.payBtnColor}
            disabled={redirecting}
            onClick={() => this.submitCardForm()}
          >
            {redirecting ? (
              <CircularProgress color="inherit" size={30} />
            ) : (
              `Pay ${totalAmount}`
            )}
          </Button>
        </Grid>
      </Grid>
    );
  }
}

CardForm.propTypes = {
  charge: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    testMode: PropTypes.bool.isRequired,
    customerId: PropTypes.string,
    due: PropTypes.number.isRequired,
    currency: PropTypes.string,
    schedule: PropTypes.shape(),
    feeRenderMode: PropTypes.string,
    recurring: PropTypes.bool,
    paymentFamilies: PropTypes.shape({
      surcharges: PropTypes.arrayOf(PropTypes.shape())
    }).isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  redirecting: PropTypes.bool.isRequired,
  history: PropTypes.shape().isRequired,
  isVpos: PropTypes.bool,
  classes: PropTypes.shape()
};

CardForm.defaultProps = {
  isVpos: false,
  classes: PropTypes.shape()
};

function mapStateToProps({ redirectionReducer }) {
  return redirectionReducer;
}

export default withStyles(styles, { withTheme: true })(
  withRouter(connect(mapStateToProps)(CardForm))
);
